import React, { useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';

import Modal from '../../modules/Modal';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { findSearchValue } from '../../../helpers';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setComposerMarkerPosition, triggerRefresh, updateCurrentSelectedComposerClips } from '../../../store/composers/composersSlice';
import { triggerRefetch, updateSecondaySelectedClips } from '../../../store/sharedStore/sharedStoreSlice';
import styles from './DeleteComposerModal.module.scss';
import {
  createClipForTopic,
  deleteComposerClip,
  deleteTopicInComposer,
  deleteTopicInReflexComposer,
  getClipsCommentsByClipIds,
} from '../../../api';
import { updateNotes, updateQualifiers } from '../../../store/videos/videoSlice';
import { UndoAction } from '..';
import { useHotkeys } from 'react-hotkeys-hook';

type TDeleteComposerClipModal = {
  switchModal: () => void;
  open: boolean;
  switchAdditionalDeleteComposerModal: () => void;
  setUndoStack: (arg: any) => void;
  clipUndoMapRef: React.MutableRefObject<Record<string, UndoAction[]>>;
  beforeMutatingComposer?: () => Promise<boolean>;
};

const DeleteComposerClipModal: React.FC<TDeleteComposerClipModal> = ({
  switchModal,
  open,
  switchAdditionalDeleteComposerModal,
  setUndoStack,
  clipUndoMapRef,
  beforeMutatingComposer,
}) => {
  const [loading, setLoading] = useState(false);
  const notification = useSnackbar();
  const { currentSelectedComposerClips, currentSelectedComposerRows, composerRows } = useAppSelector((state) => state.composersStore);
  const { secondarySelectedClips } = useAppSelector((state) => state.sharedStore);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const clipsToDelete = useMemo(() => {
    return secondarySelectedClips.length ? secondarySelectedClips : currentSelectedComposerClips;
  }, [secondarySelectedClips, currentSelectedComposerClips]);
  const selectedRowsToDelete = useMemo(() => {
    if (secondarySelectedClips.length || !currentSelectedComposerRows.length) return [];

    const selectedRowIds = new Set(currentSelectedComposerRows.map((row) => row.id));
    return composerRows
      .filter((row) => selectedRowIds.has(row.id))
      .map((row) => ({
        id: row.id,
        name: row.name,
        clipsCount: row.clips.length,
      }));
  }, [composerRows, currentSelectedComposerRows, secondarySelectedClips.length]);
  const shouldDeleteRows = selectedRowsToDelete.length > 0;

  const onClose = () => {
    dispatch(updateSecondaySelectedClips([]));
    switchModal();
  };

  const pushUndo = (action: UndoAction) => setUndoStack((s: UndoAction[]) => [...s, action]);

  const onClipDelete = async () => {
    const feedId = findSearchValue('feedId') as string;
    const pageType = findSearchValue('pageType') as string;
    if (beforeMutatingComposer) {
      const canProceed = await beforeMutatingComposer();
      if (!canProceed) return;
    }
    setLoading(true);
    try {
      if (shouldDeleteRows) {
        for (const row of selectedRowsToDelete) {
          switch (pageType) {
            case 'MY_COMPOSER':
              await deleteTopicInReflexComposer(row.id, feedId);
              break;
            case 'COMPOSER':
              await deleteTopicInComposer(row.id, feedId);
              break;
            default:
              break;
          }
        }

        if (composerRows.length === selectedRowsToDelete.length) {
          switchAdditionalDeleteComposerModal();
        }

        dispatch(triggerRefetch());
        onClose();
        return;
      }

      const clipsBackup = await Promise.all(
        clipsToDelete.map(async (c: any) => {
          const topicId = c.topicId ?? composerRows.find((row) => row.clips.some((clip) => String(clip.id) === String(c.id)))?.id;
          if (!topicId) throw new Error('Cannot delete a clip without its topic.');
          const { data: comments } = await getClipsCommentsByClipIds([+c.id]);
          const clipObj = { ...c, comments, topicId };
          if (clipObj?.clip) {
            delete clipObj.clip;
          }
          return clipObj;
        }),
      );
      // 1) Build the UndoAction
      const undoAction: UndoAction = {
        exec: async (topicIdOverride?: number) => {
          for (const clip of clipsBackup) {
            await createClipForTopic(
              {
                topic_id: topicIdOverride ?? +clip.topicId!,
                time_start: clip.time_start,
                time_end: clip.time_end,
                color: clip.color,
                qualifiers: clip.qualifiers,
                // @ts-ignore
                notes: clip.notes,
                comments: clip.comments,
                // @ts-ignore
                clipIndex: clip.clipIndex,
              },
              +feedId,
            );
          }
        },
      };

      // 2) Push it onto the undo stack
      pushUndo(undoAction);

      for (const clip of clipsBackup) {
        const tid = clip.topicId.toString();
        clipUndoMapRef.current[tid] = clipUndoMapRef.current[tid] || [];
        clipUndoMapRef.current[tid].push(undoAction);
      }

      await deleteComposerClip(
        feedId,
        clipsToDelete.map((c) => c.id),
      );
      let clipsCount = 0;
      composerRows.forEach((t) => {
        clipsCount += t.clips.length;
      });
      if (clipsCount === clipsToDelete.length) {
        switchAdditionalDeleteComposerModal();
        dispatch(updateNotes([]));
        dispatch(updateQualifiers([]));
      }
      dispatch(triggerRefetch());
      if (secondarySelectedClips.length) {
        dispatch(updateSecondaySelectedClips([]));
      } else if (currentSelectedComposerClips.length) {
        const nextRowIndex = composerRows.findIndex((r) => r.clips.length > 0);
        if (nextRowIndex !== -1) {
          dispatch(setComposerMarkerPosition({ rowIndex: nextRowIndex, clipIndex: 0 }));
          const firstClip = composerRows[nextRowIndex].clips[0];
          dispatch(
            updateCurrentSelectedComposerClips([
              {
                rowIndex: nextRowIndex,
                clipIndex: 0,
                ...firstClip,
                topicId: composerRows[nextRowIndex].id,
                topicName: composerRows[nextRowIndex].name,
              },
            ]),
          );
          dispatch(triggerRefresh());
        }
      }
      onClose();
    } catch (err: any) {
      console.log(err);
      if (err?.response?.data?.message) notification.showMessage(err.response.data.message);
      else notification.showMessage('Please try again');
    } finally {
      dispatch(updateSecondaySelectedClips([]));
      setLoading(false);
    }
  };

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={!loading ? onClose : console.log} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>
          {shouldDeleteRows
            ? `Would you like to delete selected topic${selectedRowsToDelete.length > 1 ? 's' : ''}?`
            : `Would you like to delete selected clip${clipsToDelete.length > 1 ? 's' : ''}?`}
        </div>
        <div>
          {shouldDeleteRows ? (
            <div className={styles.selectedList}>
              <div>Selected topic{selectedRowsToDelete.length > 1 && 's'}:</div>
              {selectedRowsToDelete.map((row) => (
                <div key={`${row.id}_selected_row`}>{row.name}</div>
              ))}
            </div>
          ) : (
            clipsToDelete.length > 0 && (
              <div className={styles.selectedList}>
                <div>Selected clip{clipsToDelete.length > 1 && 's'}:</div>
                {clipsToDelete.map((c) => (
                  <div key={`${c?.id}_selected_clip`}>
                    {composerRows[c?.rowIndex!]?.name} : ({c?.clipIndex! + 1}){' '}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
        <div className={styles.form}>
          <div className={styles.actionBtns}>
            <OutlinedButton onClick={onClipDelete} kind="default" disabled={loading}>
              <div ref={submitButtonRef}>Yes</div>
            </OutlinedButton>

            <ContainedButton kind="purple" onClick={onClose} disabled={loading}>
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteComposerClipModal;
